// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { findFolderById, selectFolderList } from '../folderListSelector';
import { getAllFoldersThunk, getAllProjectsInFolderThunk } from '../folderListThunk';
import { SetProjectsInFolderAction } from '../folderListActions';

type LoadStatus = 'loading' | 'loaded' | 'failed';

export interface IFolderPage {
  // The folder, read from the store on every render so an inline rename shows at once.
  folder: IFolder | null;
  // The organization's folders were read for this page and the folder is not among them.
  folderNotFound: boolean;
  // The folders could not be read and the store does not know the folder either.
  folderUnreadable: boolean;
  projectsLoading: boolean;
  projectsFailed: boolean;
  reloadFolders: () => Promise<void>;
  reloadProjects: () => Promise<void>;
}

// The folder page's own reads, for one folder (the page is mounted once per folder id). It reads the organization's
// folders, so a link to a deleted folder, or one opened from the address bar, knows whether the folder exists and
// what it is called; and it always reads the folder's projects, whatever count the folder card last showed. The
// folder's projects never outlive the page: they are cleared when it opens and when it closes, and an answer that
// arrives after a newer read, or after the page closed, is dropped.
export const useFolderPage = (folderId: string | undefined): IFolderPage => {
  const dispatch = useAppDispatch();
  const folderList = useAppSelector(selectFolderList);
  const folder = findFolderById(folderList, folderId) ?? null;
  const [folderListStatus, setFolderListStatus] = useState<LoadStatus>('loading');
  const [projectsStatus, setProjectsStatus] = useState<LoadStatus>('loading');
  // Each projects read takes the next number; only the latest may write the page's list.
  const latestRead = useRef(0);
  // Whether the page is still open, so a read that answers after it closed changes nothing.
  const open = useRef(false);

  const reloadFolders = useCallback(async () => {
    setFolderListStatus('loading');
    try {
      await dispatch(getAllFoldersThunk());
      if (open.current) setFolderListStatus('loaded');
    } catch (error) {
      if (open.current) setFolderListStatus('failed');
    }
  }, [dispatch]);

  const reloadProjects = useCallback(async () => {
    if (!folderId) return;
    latestRead.current += 1;
    const read = latestRead.current;
    setProjectsStatus('loading');
    try {
      const projects = await dispatch(getAllProjectsInFolderThunk(folderId));
      if (read !== latestRead.current) return;
      dispatch(SetProjectsInFolderAction(projects));
      setProjectsStatus('loaded');
    } catch (error) {
      if (read === latestRead.current) setProjectsStatus('failed');
    }
  }, [dispatch, folderId]);

  useEffect(() => {
    const reads = latestRead;
    const isOpen = open;
    isOpen.current = true;
    dispatch(SetProjectsInFolderAction([]));
    reloadFolders();
    reloadProjects();
    return () => {
      isOpen.current = false;
      reads.current += 1;
      dispatch(SetProjectsInFolderAction([]));
    };
  }, [dispatch, reloadFolders, reloadProjects]);

  return {
    folder,
    folderNotFound: !folder && folderListStatus === 'loaded',
    folderUnreadable: !folder && folderListStatus === 'failed',
    projectsLoading: projectsStatus === 'loading',
    projectsFailed: projectsStatus === 'failed',
    reloadFolders,
    reloadProjects,
  };
};

export default useFolderPage;
